import { forwardRef, useState, useEffect } from "react";

const MAX_TAGS = 6;

const FormInputField = forwardRef((props, ref) => {
  const {
    label,
    bottomLabel,
    name,
    altLabel = "",
    disabled = false,
    required = false,
    placeholder = "",
    defaultValue = "",
    type = "text",
    checked,
    itemName,
    items,
    disabledChoiseText = "",
    error,
    onTagsChange,
    min,
    max,
  } = props;

  // Initialize tags from defaultValue (space-separated string)
  const [tags, setTags] = useState(
    typeof defaultValue === "string" && defaultValue.length > 0
      ? defaultValue.split(" ").filter((tag) => tag.trim() !== "")
      : [],
  );
  const [tagInput, setTagInput] = useState("");

  // Sync tags if defaultValue changes (for edit mode)
  useEffect(() => {
    setTags(
      typeof defaultValue === "string" && defaultValue.length > 0
        ? defaultValue.split(" ").filter((tag) => tag.trim() !== "")
        : [],
    );
  }, [defaultValue]);

  const handleTagInput = (e) => {
    const value = e.target.value;
    if (
      (value.endsWith(" ") || value.endsWith(",")) &&
      tags.length < MAX_TAGS
    ) {
      const newTag = value.trim().replace(/,$/, "");
      if (newTag && !tags.includes(newTag)) {
        const updatedTags = [...tags, newTag];
        setTags(updatedTags);
        if (onTagsChange) onTagsChange(updatedTags);
      }
      setTagInput("");
    } else {
      setTagInput(value);
    }
  };

  const removeTag = (removeIdx) => {
    const updatedTags = tags.filter((_, idx) => idx !== removeIdx);
    setTags(updatedTags);
    if (onTagsChange) onTagsChange(updatedTags);
  };

  const renderInputField = () => {
    switch (type) {
      case "checkbox":
        return (
          <div className="form-control w-52 my-4">
            <label className="cursor-pointer label">
              <span className="label-text text-base-content pr-2">{label}</span>
              <input
                type="checkbox"
                name={name}
                required={required}
                defaultChecked={checked}
                className="toggle toggle-success"
                ref={ref}
              />
            </label>
          </div>
        );

      case "select":
        return (
          <div className="my-4 max-w-xs flex flex-col">
            <div className="label">
              <span className="label-text text-base-content pr-2">{label}</span>
            </div>
            <select
              name={name}
              required={required}
              defaultValue={defaultValue}
              className="select select-bordered w-full max-w-xs"
              ref={ref}
            >
              {disabledChoiseText && (
                <option value="" disabled>
                  {disabledChoiseText}
                </option>
              )}

              {items &&
                items.map((item, index) => {
                  // Check if item is an object with 'id' and 'name' or just a string
                  if (typeof item === "object" && item !== null) {
                    return (
                      <option key={index} value={item.id}>
                        {item[itemName]}
                      </option>
                    );
                  } else {
                    // If it's just a list, fallback to the previous handling
                    return (
                      <option key={index} value={item[itemName]}>
                        {item[itemName]}
                      </option>
                    );
                  }
                })}
            </select>
          </div>
        );

      case "file":
        return (
          <div className="my-4 flex flex-col">
            <label className="form-control w-full max-w-xs">
              <div className="label">
                <span className="label-text text-base-content pr-2">
                  {label}
                </span>
              </div>
              <input
                type="file"
                required={required}
                name={name}
                className="file-input file-input-bordered w-full max-w-xs"
                ref={ref}
                accept=".webp,.jpeg,.jpg,.png"
              />
              {bottomLabel && (
                <div className="label">
                  <span className="label-text-alt">{bottomLabel}</span>
                </div>
              )}
            </label>
          </div>
        );

      case "textarea":
        return (
          <label className="form-control max-w-xl my-4">
            <div className="label">
              <span className="label-text text-base-content pr-2">{label}</span>
              {altLabel && <span className="label-text-alt">{altLabel}</span>}
            </div>
            <textarea
              className="textarea textarea-bordered h-96 w-full mb-4"
              type={type}
              name={name}
              disabled={disabled}
              required={required}
              placeholder={placeholder}
              defaultValue={defaultValue}
              ref={ref}
            ></textarea>
            {bottomLabel && (
              <div className="label">
                <span className="label-text-alt">{bottomLabel}</span>
              </div>
            )}
          </label>
        );

      case "tags":
        return (
          <label className="form-control max-w-xl my-4">
            <div className="label">
              <span className="label-text text-base-content pr-2">{label}</span>
              {altLabel && <span className="label-text-alt">{altLabel}</span>}
            </div>
            <div className="flex flex-wrap gap-2 mb-2">
              {tags.map((tag, idx) => (
                <span
                  key={idx}
                  className="bg-base-300 text-base-content px-3 py-1 rounded-full flex items-center"
                >
                  {tag}
                  <button
                    type="button"
                    className="ml-2 text-xs text-red-500 hover:text-red-700"
                    onClick={() => removeTag(idx)}
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
            <input
              type="text"
              name="tags_input"
              disabled={disabled || tags.length >= MAX_TAGS}
              required={required && tags.length === 0}
              placeholder={
                tags.length >= MAX_TAGS ? "Max 6 taggar" : placeholder
              }
              value={tagInput}
              onChange={handleTagInput}
              className="input input-bordered w-full"
              ref={ref}
              onKeyDown={(e) => {
                if (
                  (e.key === "Enter" || e.key === " " || e.key === ",") &&
                  tagInput.trim() &&
                  tags.length < MAX_TAGS
                ) {
                  e.preventDefault();
                  const newTag = tagInput.trim().replace(/,$/, "");
                  if (newTag && !tags.includes(newTag)) {
                    const updatedTags = [...tags, newTag];
                    setTags(updatedTags);
                    if (onTagsChange) onTagsChange(updatedTags);
                  }
                  setTagInput("");
                }
              }}
            />
            {/* Hidden input for form submission */}
            <input type="hidden" name={name} value={tags.join(" ")} />
            {bottomLabel && (
              <div className="label">
                <span className="label-text-alt">{bottomLabel}</span>
              </div>
            )}
          </label>
        );

      default:
        return (
          <label className="form-control max-w-xs flex flex-col my-4">
            <div className="label">
              <span className="label-text text-base-content pr-2">
                {label}
              </span>{" "}
            </div>
            <input
              type={type}
              name={name}
              disabled={disabled}
              required={required}
              min={min}
              max={max}
              placeholder={placeholder}
              defaultValue={defaultValue}
              className="input input-bordered max-w-xs"
              ref={ref}
            />
            {bottomLabel && (
              <div className="label">
                <span className="label-text-alt">{bottomLabel}</span>
              </div>
            )}
          </label>
        );
    }
  };

  return <>{renderInputField()}</>;
});

FormInputField.displayName = "FormInputField";

export default FormInputField;
